import { Box, Grid, InputAdornment, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ExternalLink } from "ExternalLink";
import { OLPaper } from "OLPaper";
import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import { OLCheckbox } from "components/FormControls/OLCheckbox";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { alphabetizeByProperty } from "utilities/arrays";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { pluralizeUnit } from "utilities/strings";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    OLPaper: {
      display: "flex",
      flexDirection: "column",
    },
    itemForm: {
      maxWidth: "100%",
      width: "100%",
      "& + &": { marginTop: theme.spacing(10) },
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": { marginTop: 0 },
    },
    info: {
      fontSize: 14,
      color: theme.gray500,
    },
    formContainer: {
      maxWidth: "872px",
      margin: `${theme.spacing(8)} auto 0`,
    },
  }),
);

export function TierConfiguration() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { updateSettings } = useSettings();
  const settingsData = useBasicSettingsData("tiers");
  const [walletTypeSearch, setWalletTypeSearch] = useState<string>();
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { useWalletTypeList } = useWallets();
  const { data: initialWalletData } = useWalletTypeList({
    filters: settingsData.formValues?.tierWalletCode
      ? [
          {
            condition: "eq",
            query: "code",
            value: settingsData.formValues.tierWalletCode,
          },
        ]
      : [],
  });

  const { data: walletTypeList } = useWalletTypeList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfTiers" }),
      path: links.tiersList,
    },
  };

  return (
    <>
      <TopHeader
        title={message({ id: "tiersConfiguration" })}
        breadcrumbs={breadcrumbs}
        clearLink={links.tiersList}
      />
      <div className={classes.formContainer}>
        {settingsData.formValues && initialWalletData ? (
          <Formik
            enableReinitialize
            initialStatus={{}}
            initialValues={{
              levelDowngradeMode:
                settingsData.formValues.levelDowngradeMode || "none",
              levelDowngradeBase: settingsData.formValues.levelDowngradeBase,
              levelDowngradeDays: settingsData.formValues.levelDowngradeDays,
              tierAssignType: settingsData.formValues.tierAssignType,
              tierWalletCode: settingsData.formValues.tierWalletCode,
              levelResetPointsOnDowngrade:
                settingsData.formValues.levelResetPointsOnDowngrade,
            }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});
              try {
                const response = await updateSettings({
                  ...values,
                  ...(values.tierAssignType === "transactions" && {
                    levelDowngradeBase: null,
                    levelDowngradeDays: null,
                    levelDowngradeMode: null,
                  }),
                  ...(values.levelDowngradeMode === "automatic" && {
                    levelDowngradeBase: null,
                    levelDowngradeDays: null,
                  }),
                  ...(values.levelDowngradeMode === "none" && {
                    levelDowngradeBase: null,
                  }),
                });
                if (response?.data?.settings) {
                  navigate(links.tiersList);
                  notify(message({ id: "settingsSaved" }));
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              status,
              values,
              dirty,
              initialValues,
            }) => {
              const unitsName =
                walletTypeList?.items?.find(
                  (wallet) => wallet.code === values.tierWalletCode,
                )?.unitPluralName || defaultWallet?.unitPluralName;

              return (
                <Form onSubmit={handleSubmit}>
                  {status?.[""] && (
                    <span className={classes.error}>{status[""]}</span>
                  )}
                  <OLPaper className={classes.OLPaper}>
                    <Typography className={classes.info}>
                      {message(
                        {
                          id: "tierConfigurationInfo",
                        },
                        {
                          link: (
                            <ExternalLink path="https://help.openloyalty.io/main-features/tiers/tiers-configuration">
                              {message({
                                id: "learnMoreAboutTierConfiguration",
                              })}
                            </ExternalLink>
                          ),
                        },
                      )}
                    </Typography>
                    <Box mt={13} />
                    <Grid container spacing={10}>
                      <Grid xs={12} item>
                        <SelectInput
                          className={classes.itemForm}
                          items={alphabetizeByProperty(
                            [
                              {
                                id: "points",
                                label: message({ id: "unitTransfers" }),
                              },
                              {
                                id: "transactions",
                                label: message({
                                  id: "transactions",
                                }),
                              },
                            ],
                            "label",
                          )}
                          label={
                            <RequiredFieldLabel
                              text={message({ id: "tierAssignType" })}
                            />
                          }
                          name="tierAssignType"
                          data-test-id="assign-type"
                        />
                        {values.tierAssignType === "transactions" && (
                          <Typography className={classes.info} mt={4}>
                            {message({
                              id: "tierCalculatedWithTransactionsInfo",
                            })}
                          </Typography>
                        )}
                      </Grid>
                      {values.tierAssignType === "points" && (
                        <>
                          <Grid xs={12} item>
                            <SelectInput
                              displaySearch
                              label={
                                <RequiredFieldLabel
                                  text={message({
                                    id: "walletType",
                                  })}
                                />
                              }
                              name="tierWalletCode"
                              placeholder={message({
                                id: "selectWalletType",
                              })}
                              data-test-id="wallet-type"
                              width="100%"
                              items={
                                walletTypeList?.items.map((wallet) => ({
                                  label: wallet.name,
                                  id: wallet.code,
                                })) || []
                              }
                              asyncSearch={(searchedQuery: string) =>
                                setWalletTypeSearch(searchedQuery)
                              }
                              initialValue={
                                initialWalletData?.items && {
                                  id: initialWalletData.items[0]?.code,
                                  label: initialWalletData.items[0]?.name,
                                }
                              }
                            />
                          </Grid>
                          <Grid xs={12} item>
                            <SelectInput
                              className={classes.itemForm}
                              items={[
                                {
                                  id: "none",
                                  label: message({
                                    id: "none",
                                  }),
                                },
                                {
                                  id: "automatic",
                                  label: message({
                                    id: "automatic",
                                  }),
                                },
                                {
                                  id: "after_x_days",
                                  label: message({
                                    id: "everyXNumberOfDays",
                                  }),
                                },
                              ]}
                              label={
                                <RequiredFieldLabel
                                  text={message({
                                    id: "recalculationSettings",
                                  })}
                                />
                              }
                              name="levelDowngradeMode"
                              data-test-id="recalculation-settings"
                            />
                          </Grid>
                          {values.levelDowngradeMode === "after_x_days" && (
                            <>
                              <Grid xs={6} item>
                                <TextInput
                                  className={classes.itemForm}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {message({
                                          id: "days",
                                        })}
                                      </InputAdornment>
                                    ),
                                  }}
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: "downgradeEvery",
                                      })}
                                    />
                                  }
                                  name="levelDowngradeDays"
                                  type="number"
                                  data-test-id="downgrade-every"
                                />
                              </Grid>
                              <Grid xs={6} item>
                                <SelectInput
                                  className={classes.itemForm}
                                  items={[
                                    {
                                      id: "active_points",
                                      label: message(
                                        {
                                          id: "activeUnitSingularOrPlural",
                                        },
                                        {
                                          units: unitsName,
                                        },
                                      ),
                                    },
                                    {
                                      id: "earned_points_since_last_level_change",
                                      label: message(
                                        {
                                          id: "earnedUnitsSinceLastTierChange",
                                        },
                                        {
                                          units: unitsName,
                                        },
                                      ),
                                    },
                                    {
                                      id: "earned_points",
                                      label: message(
                                        {
                                          id: "earnedUnitsWithinLastXDays",
                                        },
                                        {
                                          units: unitsName,
                                        },
                                      ),
                                    },
                                  ]}
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: "downgradeBasedOn",
                                      })}
                                    />
                                  }
                                  name="levelDowngradeBase"
                                  data-test-id="downgrade-based-on"
                                />
                              </Grid>
                              {values.levelDowngradeBase ===
                                "active_points" && (
                                <Grid item>
                                  <OLCheckbox
                                    label={message(
                                      {
                                        id: "resetUnits",
                                      },
                                      {
                                        units: unitsName,
                                        numberOfDays:
                                          values?.levelDowngradeDays &&
                                          values.levelDowngradeDays > 0
                                            ? pluralizeUnit(
                                                values.levelDowngradeDays,
                                                "day",
                                                "days",
                                              )
                                            : message({
                                                id: "day",
                                              }).toLowerCase(),
                                      },
                                    )}
                                    name="levelResetPointsOnDowngrade"
                                    data-test-id="reset-units"
                                  />
                                </Grid>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </OLPaper>
                  <Box mt={12} />
                  <FormButtons
                    disableSubmit={isSubmitting}
                    onCancelClick={() => navigate(links.tiersList)}
                    onSubmitClick={() => handleSubmit()}
                  />
                  {!isSubmitting && (
                    <LeaveSiteGuard
                      warnBeforeRefresh
                      initialValues={initialValues}
                      currentValues={values}
                      isDirty={dirty}
                    />
                  )}
                </Form>
              );
            }}
          </Formik>
        ) : (
          <>
            <Skeleton height={300} width="100%" />
            <Box mt={12} />
            <FormButtons
              disableSubmit
              onCancelClick={() => {}}
              onSubmitClick={() => {}}
            />
          </>
        )}
      </div>
    </>
  );
}
